import { ArrowLeftOutlined } from '@ant-design/icons'
import { PageHeader } from '@ant-design/pro-layout'
import { useMutation, useSuspenseQuery } from '@apollo/client'
import {
  type CommitmentKpiLabel,
  type CommitmentKpiSchedule,
  FetchCommitmentKpiLabelsDocument,
  FetchGoalConfigFormRelatedDocument,
  type GoalConfigFieldsFragment,
  UpdateGoalConfigAndRemoveCommitmentKpiLabelDocument,
  UpdateGoalConfigDocument,
  UpdateGoalConfigWithCommitmentKpiLabelDocument,
} from '@salescore/client-api'
import {
  buildCommitmentKpiScheduleEntry,
  buildCommitmentKpiScheduleFormValues,
  ErrorContext,
  getOrganizationIdFromPath,
  HandleQuery,
  useCan,
  useMe,
  ViewPicker,
} from '@salescore/client-common'
import { commitmentKpiScheduleFormValuesSchema } from '@salescore/client-feature-view'
import { Button, Col, Form, Input, message, Row, Select, Skeleton } from 'antd'
import { useForm } from 'antd/es/form/Form'
import { isNotNullish } from 'effect/Predicate'
import { t } from 'i18next'
import { type ReactNode, useContext } from 'react'
import { z } from 'zod'

import { CommitmentKpiLabelItemForm, showCommitmentKpiLabelItemForm } from './CommitmentKpiLabelItemForm'
import { UserAndGroupPickerFormItem } from './CreateGoalConfigForm'

export const commitmentKpiLabelPeriodSchema = z.object({
  startAt: z.string(),
  endAt: z.string(),
})
const commitmentKpiLabelSchema = z.discriminatedUnion('action', [
  z.object({
    action: z.literal('set'),
    viewId: z.string(),
    periods: z.array(commitmentKpiLabelPeriodSchema),
    schedule: commitmentKpiScheduleFormValuesSchema,
  }),
  z.object({
    action: z.literal('remove'),
  }),
  z.object({
    action: z.literal('keep'),
  }),
])

const goalConfigSchema = z.object({
  name: z.string(),
  userType: z.string(),
  userAndGroupIdsConfig: z.object({ key: z.string(), value: z.string() }).array().optional(),
  commitmentKpiLabel: commitmentKpiLabelSchema,
})
export type FormValue = z.infer<typeof goalConfigSchema>

export const goalConfigTimeSpanTypeOptions = [
  {
    value: 'month',
    label: t(`月`),
  },
  {
    value: 'week',
    label: t(`週`),
  },
  {
    value: 'day',
    label: t(`日`),
  },
]

export const goalConfigUserTypeOptions = [
  {
    value: 'user',
    label: t(`ユーザー`),
  },
  {
    value: 'group',
    label: t(`ユーザーグループ`),
  },
  {
    value: 'none',
    label: t(`ユーザーに依存しない目標`),
  },
]

export const UpdateGoalConfigForm = ({
  goalConfig,
  onAfterFinish,
  onBack,
}: {
  goalConfig: GoalConfigFieldsFragment
  onBack?: () => void
  onAfterFinish: () => void
}): ReactNode => (
  <HandleQuery loadingElement={<Skeleton />}>
    <Body goalConfig={goalConfig} onAfterFinish={onAfterFinish} onBack={onBack} />
  </HandleQuery>
)

// eslint-disable-next-line sonarjs/prefer-read-only-props
function Body({
  goalConfig,
  onAfterFinish,
  onBack,
}: {
  goalConfig: GoalConfigFieldsFragment
  onAfterFinish: () => void
  onBack?: () => void
}): ReactNode {
  const {
    data: { goalDimensions },
  } = useSuspenseQuery(FetchGoalConfigFormRelatedDocument, {
    variables: {
      organizationId: getOrganizationIdFromPath(),
    },
  })
  const {
    data: { commitmentKpiLabels },
  } = useSuspenseQuery(FetchCommitmentKpiLabelsDocument, {
    variables: {
      organizationId: getOrganizationIdFromPath(),
      goalConfigId: goalConfig.id,
    },
    fetchPolicy: 'no-cache',
  })
  const commitmentKpiSchedule = commitmentKpiLabels[0]?.schedule ?? null
  const me = useMe()
  const [form] = useForm<FormValue>()
  const [updateGoalConfigMutation, { loading }] = useMutation(UpdateGoalConfigDocument)
  const [updateGoalConfigWithCommitmentKpiLabelMutation, { loading: loadingWithCommitmentKpiLabel }] = useMutation(
    UpdateGoalConfigWithCommitmentKpiLabelDocument,
  )
  const [updateGoalConfigAndRemoveCommitmentKpiLabelMutation, { loading: loadingAndRemoveCommitmentKpiLabel }] =
    useMutation(UpdateGoalConfigAndRemoveCommitmentKpiLabelDocument)
  const errorContext = useContext(ErrorContext)
  const canManageGoal = useCan('manage-goal')
  const onFinish = (values: unknown): void => {
    const formValue = goalConfigSchema.parse(values)
    const userGroupIdsConfig =
      formValue.userAndGroupIdsConfig?.filter((x) => x.key.endsWith('group')).map((x) => x.value) ?? []
    const userIdsConfig =
      formValue.userAndGroupIdsConfig?.filter((x) => x.key.endsWith('user')).map((x) => x.value) ?? []

    if (formValue.userType === 'user' && [...userGroupIdsConfig, ...userIdsConfig].length === 0) {
      void message.error(t(`目標を設定するチーム・ユーザーを指定してください`))
      return
    }
    if (goalConfig.userType === 'group' && userGroupIdsConfig.length === 0) {
      void message.error(t(`目標を設定するチームを指定してください`))
      return
    }

    if (formValue.commitmentKpiLabel.action === `keep`) {
      void updateGoalConfigMutation({
        variables: {
          organizationId: getOrganizationIdFromPath(),
          goalConfig: {
            id: goalConfig.id,
            name: formValue.name,
            userGroupIdsConfig,
            userIdsConfig,
            rank: goalConfig.rank,
          },
        },
        onCompleted: () => {
          void message.info(t(`目標設定を更新しました！`))
          onAfterFinish() // redirect
        },
        onError: (error) => {
          errorContext.throwErrors(error)
        },
      })
    } else if (formValue.commitmentKpiLabel.action === `remove`) {
      void updateGoalConfigAndRemoveCommitmentKpiLabelMutation({
        variables: {
          organizationId: getOrganizationIdFromPath(),
          goalConfig: {
            id: goalConfig.id,
            name: formValue.name,
            userGroupIdsConfig,
            userIdsConfig,
            rank: goalConfig.rank,
          },
          goalConfigId: goalConfig.id,
        },
        onCompleted: () => {
          void message.info(t(`目標設定を更新しました！`))
          onAfterFinish()
        },
        onError: (error) => {
          errorContext.throwErrors(error)
        },
      })
    } else {
      const setLabel = formValue.commitmentKpiLabel
      void updateGoalConfigWithCommitmentKpiLabelMutation({
        variables: {
          organizationId: getOrganizationIdFromPath(),
          goalConfig: {
            id: goalConfig.id,
            name: formValue.name,
            userGroupIdsConfig,
            userIdsConfig,
            rank: goalConfig.rank,
          },
          commitmentKpiLabel: {
            viewId: setLabel.viewId,
            periods: setLabel.periods,
            goalConfigId: goalConfig.id,
            scheduleEntry: buildCommitmentKpiScheduleEntry(setLabel.schedule),
          },
        },
        onCompleted: () => {
          void message.info(t(`目標設定を更新しました！`))
          onAfterFinish()
        },
        onError: (error) => {
          errorContext.throwErrors(error)
        },
      })
    }
  }

  if (!canManageGoal) {
    return (
      <PageHeader title={t(`目標設定を編集`)} onBack={onBack}>
        {t(`設定権限がありません。`)}
      </PageHeader>
    )
  }

  const action: 'set' | 'remove' | 'keep' = showCommitmentKpiLabelItemForm(me, goalConfig)
    ? // eslint-disable-next-line sonarjs/no-nested-conditional
      commitmentKpiLabels.length > 0
      ? 'set'
      : 'remove'
    : 'keep'

  const commitmentKpiLabelInitial = buildCommitmentKpiLabelInitial(
    action,
    commitmentKpiLabels,
    commitmentKpiSchedule ?? null,
  )
  const goalConfigInitial: Partial<FormValue> = {
    ...goalConfig,
    userAndGroupIdsConfig: [
      ...goalConfig.userGroupIdsConfig.map((x) => ({ key: `${x}-group`, value: x })),
      ...goalConfig.userIdsConfig.map((x) => ({ key: `${x}-user`, value: x })),
    ],
    commitmentKpiLabel: commitmentKpiLabelInitial,
  }
  return (
    <PageHeader onBack={onBack}>
      <Form<FormValue> form={form} onFinish={onFinish} layout="vertical" initialValues={goalConfigInitial}>
        <Row>
          <Col>
            <Button type="text" icon={<ArrowLeftOutlined />} onClick={onBack} className="my-3" />
          </Col>
          <Col span={20}>
            <Form.Item name="name" rules={[{ required: true, message: t(`目標設定の名前を入力してください`) }]}>
              <Input
                size="large"
                variant="borderless"
                placeholder="目標設定の名前を入力"
                style={{ fontSize: 24, fontWeight: 'bold' }}
              />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="dashboardViewId" label={t(`ダッシュボード`)} required>
          <ViewPicker disabled />
        </Form.Item>
        <Form.Item name="timeSpanType" label={t(`時間軸`)} required>
          <Select disabled options={goalConfigTimeSpanTypeOptions} />
        </Form.Item>
        <Form.Item name="userType" label={t(`ユーザー軸`)} required>
          <Select disabled options={goalConfigUserTypeOptions} />
        </Form.Item>
        {goalConfig.userType === 'user' && (
          <>
            <UserAndGroupPickerFormItem />
          </>
        )}
        {goalConfig.userType === 'group' && (
          <>
            <UserAndGroupPickerFormItem label={t(`チームを指定`)} />
          </>
        )}
        {
          // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
          goalConfig.goalDimension1 && (
            <Form.Item name="goalDimension1Id" label={t(`目標軸1`)}>
              <Select disabled options={goalDimensions.map((x) => ({ value: x.id, label: x.label }))} />
            </Form.Item>
          )
        }
        {
          // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
          goalConfig.goalDimension2 && (
            <Form.Item name="goalDimension2Id" label={t(`目標軸2`)}>
              <Select disabled options={goalDimensions.map((x) => ({ value: x.id, label: x.label }))} />
            </Form.Item>
          )
        }
        {
          // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
          goalConfig.goalDimension3 && (
            <Form.Item name="goalDimension3Id" label={t(`目標軸3`)}>
              <Select disabled options={goalDimensions.map((x) => ({ value: x.id, label: x.label }))} />
            </Form.Item>
          )
        }
        {
          // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
          goalConfig.goalDimension4 && (
            <Form.Item name="goalDimension4Id" label={t(`目標軸4`)}>
              <Select disabled options={goalDimensions.map((x) => ({ value: x.id, label: x.label }))} />
            </Form.Item>
          )
        }
        {
          // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
          goalConfig.goalDimension5 && (
            <Form.Item name="goalDimension5Id" label={t(`目標軸5`)}>
              <Select disabled options={goalDimensions.map((x) => ({ value: x.id, label: x.label }))} />
            </Form.Item>
          )
        }
        <>
          <CommitmentKpiLabelItemForm goalConfig={goalConfig} commitmentKpiLabels={commitmentKpiLabels} form={form} />
        </>
        <Form.Item>
          <Row justify="end">
            <Button
              type="primary"
              loading={loading || loadingWithCommitmentKpiLabel || loadingAndRemoveCommitmentKpiLabel}
              htmlType="submit"
            >
              {t(`保存`)}
            </Button>
          </Row>
        </Form.Item>
      </Form>
    </PageHeader>
  )
}

function buildCommitmentKpiLabelInitial(
  action: 'set' | 'remove' | 'keep',
  commitmentKpiLabels: CommitmentKpiLabel[],
  commitmentKpiSchedule: CommitmentKpiSchedule | null,
): FormValue['commitmentKpiLabel'] {
  if (
    action === 'set' &&
    isNotNullish(commitmentKpiLabels) &&
    commitmentKpiLabels.length > 0 &&
    isNotNullish(commitmentKpiLabels[0])
  ) {
    return {
      action: 'set',
      periods: commitmentKpiLabels.map((label) => ({
        startAt: label.startAt,
        endAt: label.endAt,
      })),
      viewId: commitmentKpiLabels[0].viewId,
      schedule: buildCommitmentKpiScheduleFormValues(commitmentKpiSchedule),
    }
  }
  return action === 'remove' ? { action: 'remove' } : { action: 'keep' }
}
